import { TypographyV3 } from '@jouzen/ecom-components';
import { useReducedMotion } from 'framer-motion';
import type { MessageKey } from 'types/LocaleMessageKey';
import type { SVGIconComponent } from 'types/svgr';

import BackdropBlur from '@/app/components/BackdropBlur';
import type { MotionProps } from '@/app/components/Motion';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';

export interface AccuracyCardProps {
  readonly heading: MessageKey;
  readonly icon: SVGIconComponent;
  readonly note?: MessageKey;
  readonly percentage: number;
}

const AccuracyCard = ({
  heading,
  icon: Icon,
  note,
  percentage,
}: AccuracyCardProps) => {
  const shouldReduceMotion = useReducedMotion();
  const motionProps: MotionProps = {
    initial: {
      translateY: shouldReduceMotion ? 0 : 20,
      opacity: 0,
    },
    transition: {
      duration: 0.5,
      delay: 0.5,
    },
    viewport: {
      once: true,
    },
    whileInView: {
      translateY: 0,
      opacity: 1,
    },
  };

  return (
    <div className="relative flex flex-col gap-y-3 p-5" data-cy="accuracy-card">
      <BackdropBlur {...motionProps} variant="card" />
      <Motion {...motionProps}>
        <div className="relative flex justify-between gap-x-6">
          <TypographyV3
            color="light"
            Element="div"
            font="serif"
            variant="h2"
            height="tight"
            className="font-extralight"
          >
            {percentage.toLocaleString(undefined, { style: 'percent' })}
          </TypographyV3>
          <div className="size-6 pt-1">
            <Icon width={24} height={24} />
          </div>
        </div>
        <div className="relative text-gray-200">
          <TypographyV3 Element="div" color="light" variant="body-small">
            <RichText messageKey={heading} />
          </TypographyV3>
          {note && (
            <TypographyV3 Element="div" color="inherit" variant="eyebrow-small">
              <RichText messageKey={note} />
            </TypographyV3>
          )}
        </div>
      </Motion>
    </div>
  );
};

export default AccuracyCard;
